import { useEffect, useMemo, useState } from "react"

import { useTransportQuery } from "@/transport"

import type { RunTemplateResource } from "@/types_api/backend/transport/list_run_templates_transport_response"

export type TemplateDependencyResourceKind =
  | "agent"
  | "metric"
  | "mutation"
  | "persona"
  | "testSet"

export interface BlockingTemplate {
  displayName: string
  id: string
}

/** A selected resource an active Template still references. */
interface BlockedResource {
  id: string
  templates: BlockingTemplate[]
}

type TemplateDependenciesAvailability =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "error" }
  | {
      status: "ready"
      blocked: BlockedResource[]
      /** The resources no active Template references. */
      deletableIds: string[]
    }

const REFERENCE_FIELD_BY_KIND = {
  agent: "agent_ids",
  metric: "metric_ids",
  mutation: "mutation_ids",
  persona: "persona_ids",
  testSet: "test_set_ids",
} as const satisfies Record<
  TemplateDependencyResourceKind,
  keyof RunTemplateResource
>

interface CompletedCheck {
  availability: TemplateDependenciesAvailability
  /** The resource set and kind this result was produced for. */
  signature: string
}

/**
 * Which of `resourceIds` active Templates still reference, resolved in one pass
 * over the Template list.
 *
 * The whole set is re-checked whenever it changes, and the result is keyed by
 * the set rather than held across changes, so a check that has not caught up
 * reads as `checking` instead of answering for the previous selection. A failed
 * lookup fails closed: nothing is reported deletable.
 */
export function useTemplateDependenciesAvailability(
  resourceIds: readonly string[],
  resourceKind: TemplateDependencyResourceKind,
): TemplateDependenciesAvailability {
  const [completedCheck, setCompletedCheck] = useState<CompletedCheck | null>(
    null,
  )
  const { refetch } = useTransportQuery({
    operation: "listRunTemplates",
    options: {
      enabled: false,
      staleTime: 0,
    },
  })

  // The ids arrive as a fresh array on every render, so the check keys off
  // their value rather than the array's identity, and carries the value it
  // was built from so the effect never reads a dependency it does not declare.
  const signature = useMemo(
    () => JSON.stringify([resourceKind, resourceIds]),
    [resourceIds, resourceKind],
  )
  const hasResources = resourceIds.length > 0

  useEffect(() => {
    if (!hasResources) {
      return
    }

    let cancelled = false
    const [checkedKind, checkedIds] = parseSignature(signature)

    void refetch().then(({ data, error }) => {
      if (cancelled) {
        return
      }

      const availability: TemplateDependenciesAvailability = error
        ? { status: "error" }
        : resolveAvailability({
            resourceIds: checkedIds,
            resourceKind: checkedKind,
            templates: data?.templates ?? [],
          })

      setCompletedCheck({ availability, signature })
    })

    return () => {
      cancelled = true
    }
  }, [hasResources, refetch, signature])

  if (!hasResources) {
    return { status: "idle" }
  }
  if (completedCheck?.signature !== signature) {
    return { status: "checking" }
  }
  return completedCheck.availability
}

function parseSignature(
  signature: string,
): [TemplateDependencyResourceKind, string[]] {
  return JSON.parse(signature) as [TemplateDependencyResourceKind, string[]]
}

function resolveAvailability({
  resourceIds,
  resourceKind,
  templates,
}: {
  resourceIds: readonly string[]
  resourceKind: TemplateDependencyResourceKind
  templates: readonly RunTemplateResource[]
}): TemplateDependenciesAvailability {
  const referenceField = REFERENCE_FIELD_BY_KIND[resourceKind]
  const blocked: BlockedResource[] = []
  const deletableIds: string[] = []

  for (const resourceId of resourceIds) {
    const blockingTemplates = templates
      .filter((template) => template[referenceField]?.includes(resourceId))
      .map((template) => ({
        displayName: template.display_name,
        id: template.id,
      }))

    if (blockingTemplates.length > 0) {
      blocked.push({ id: resourceId, templates: blockingTemplates })
    } else {
      deletableIds.push(resourceId)
    }
  }

  return { status: "ready", blocked, deletableIds }
}
